<script setup lang="ts">
import type { DistributionGroupWithID, EmissionType } from '#design-tokenomics/types'
import type { ColumnDef } from '@forgd/ui-kit/blocks/data-table'
import { useNumberFieldBlurGuard } from '#design-tokenomics/composables/useNumberFieldBlurGuard'
import { ColumnHeader, DataTable, InputCell } from '@forgd/ui-kit/blocks/data-table'
import { NoData } from '@forgd/ui-kit/blocks/empty-states'
import { Button } from '@forgd/ui-kit/button'
import { InputGroup, InputGroupAddon } from '@forgd/ui-kit/input-group'
import { NumberField, NumberFieldInput } from '@forgd/ui-kit/number-field'
import { Switch } from '@forgd/ui-kit/switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '@forgd/ui-kit/tooltip'
import { ChevronUpDownIcon } from '@heroicons/vue/24/outline'

defineProps<{
  groups: Array<DistributionGroupWithID>
}>()

const emit = defineEmits<{
  moveGroup: [id: string, type: EmissionType]
}>()

const percentFormat = { maximumFractionDigits: 2 }
const percentGuard = useNumberFieldBlurGuard(percentFormat)
const monthsFormat = { maximumFractionDigits: 0 }
const monthsGuard = useNumberFieldBlurGuard(monthsFormat)

interface NumberInputOptions {
  value: number | null | undefined
  label: string
  suffix: '%' | 'Months'
  disabled?: boolean
  onUpdate: (value: number | null) => void
}

function numberInput({ value, label, suffix, disabled, onUpdate }: NumberInputOptions) {
  const guard = suffix === '%' ? percentGuard : monthsGuard
  return h(NumberField, {
    'modelValue': value ?? undefined,
    'onUpdate:modelValue': (v: number | undefined) => guard.accepts(v, value) && onUpdate(v ?? null),
    'min': 0,
    'formatOptions': suffix === '%' ? percentFormat : monthsFormat,
    'stepSnapping': false,
    'disabled': disabled,
  }, () => h(InputGroup, { size: 'sm' }, () => [
    h(NumberFieldInput, { ...guard.inputListeners, 'aria-label': label }),
    h(InputGroupAddon, null, () => suffix),
  ]))
}

const columns: ColumnDef<DistributionGroupWithID>[] = [
  {
    accessorKey: 'name',
    size: 200,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Group Name/Allocation %', class: 'pr-1 pl-2', tooltip: 'The groups and their allocations have been defined in Token Distribution. To change them, head back there.' }),
    cell: ({ row, column }) => h(InputCell, { column, class: 'flex items-center gap-2' }, () => [
      h('div', { class: 'h-10 w-1 rounded-full', style: `background-color: ${row.original.color}` }),
      h('div', {}, [
        h('div', {}, row.original.name),
        h('div', { class: 'text-xs font-semibold' }, `${row.original.allocationPercentage}%`),
      ]),
    ]),
  },
  {
    accessorKey: 'dictateDuration',
    size: 155,
    maxSize: 155,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Dictate Duration', class: 'px-1', tooltip: 'Choose whether to specify Emission Duration and let us determine the correct Reduction % per Epoch or define the Reduction % per Epoch and let us work out the duration.' }),
    cell: ({ row, column }) => h(InputCell, { column, class: 'flex items-center gap-2' }, () => [
      h(Switch, {
        'modelValue': row.original.dictateDuration ?? false,
        'size': 'lg',
        'onUpdate:modelValue': (v: boolean) => {
          row.original.dictateDuration = v
        },
      }),
      h('span', { class: 'text-xs' }, row.original.dictateDuration ? 'Yes' : 'No'),
    ]),
  },
  {
    accessorKey: 'durationInMonths',
    size: 155,
    maxSize: 155,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Emission Duration', class: 'px-1', tooltip: 'When choosing to Dictate Duration, specify how long you want this group to be emitting.' }),
    cell: ({ row, column }) => h(InputCell, { column }, () => numberInput({
      value: row.original.durationInMonths,
      label: 'Emission Duration',
      suffix: 'Months',
      disabled: !row.original.dictateDuration,
      onUpdate: (v) => {
        row.original.durationInMonths = v
      },
    })),
  },
  {
    accessorKey: 'epochInMonths',
    size: 155,
    maxSize: 155,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Epoch Duration', class: 'px-1', tooltip: 'An epoch is a timeframe in which emissions of tokens stay the same. Specify here how many months an epoch is.' }),
    cell: ({ row, column }) => h(InputCell, { column }, () => numberInput({
      value: row.original.epochInMonths,
      label: 'Epoch Duration',
      suffix: 'Months',
      onUpdate: (v) => {
        row.original.epochInMonths = v
      },
    })),
  },
  {
    accessorKey: 'firstEpochPercentage',
    size: 155,
    maxSize: 155,
    header: ({ column }) => h(ColumnHeader, { column, label: 'First Epoch %', class: 'px-1', tooltip: 'How much of the supply allocated to this group shall be emitted in the first epoch?' }),
    cell: ({ row, column }) => h(InputCell, { column }, () => numberInput({
      value: row.original.firstEpochPercentage,
      label: 'First Epoch %',
      suffix: '%',
      onUpdate: (v) => {
        row.original.firstEpochPercentage = v
      },
    })),
  },
  {
    accessorKey: 'reductionsInEpochPercentage',
    size: 155,
    maxSize: 155,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Reductions per Epoch', class: 'px-1', tooltip: 'How much shall the emissions be reduced every epoch? Use this to control how quickly your supply will get into circulation.' }),
    cell: ({ row, column }) => h(InputCell, { column }, () => numberInput({
      value: row.original.reductionsInEpochPercentage,
      label: 'Reductions per Epoch',
      suffix: '%',
      disabled: row.original.dictateDuration ?? false,
      onUpdate: (v) => {
        row.original.reductionsInEpochPercentage = v
      },
    })),
  },
  {
    id: 'move',
    size: 44,
    maxSize: 44,
    header: '',
    cell: ({ row, column }) => h(InputCell, { column }, () => h(Tooltip, () => [
      h(TooltipTrigger, { asChild: true }, () => h(Button, {
        'variant': 'outline',
        'size': 'sm',
        'iconOnly': true,
        'aria-label': 'Move this group to another emission schedule',
        'onClick': () => emit('moveGroup', row.original.id, 'epoch-based'),
      }, () => h(ChevronUpDownIcon, { 'aria-hidden': 'true' }))),
      h(TooltipContent, () => 'Move this group to another emission schedule'),
    ])),
  },
]
</script>

<template>
  <DataTable :columns="columns" :data="groups" size="md">
    <template #empty>
      <NoData message="No groups assigned to this emission type" />
    </template>
  </DataTable>
</template>
